/**
 * IST Timezone Utility for Frontend
 * India Standard Time (IST) is UTC+5:30
 *
 * This utility provides consistent timezone handling across the frontend.
 * All times should be displayed in IST for Indian users.
 */

const IST_TIMEZONE = 'Asia/Kolkata';

/**
 * Format a date for display in IST
 * @param {string|Date} dateString - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string in IST
 */
export function formatDateIST(dateString, options = {}) {
  if (!dateString) return '';

  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options
  };

  return new Date(dateString).toLocaleDateString('en-IN', defaultOptions);
}

/**
 * Format a time for display in IST
 * @param {string|Date} dateString - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted time string in IST
 */
export function formatTimeIST(dateString, options = {}) {
  if (!dateString) return '';

  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };

  return new Date(dateString).toLocaleTimeString('en-IN', defaultOptions);
}

/**
 * Format a full datetime for display in IST
 * @param {string|Date} dateString - Date string or Date object
 * @param {Object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted datetime string in IST
 */
export function formatDateTimeIST(dateString, options = {}) {
  if (!dateString) return '';

  const defaultOptions = {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    ...options
  };

  return new Date(dateString).toLocaleString('en-IN', defaultOptions);
}

/**
 * Get current date in IST as ISO string (YYYY-MM-DD)
 * @returns {string} Current date in IST
 */
export function getTodayIST() {
  const now = new Date();
  return now.toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE }); // en-CA gives YYYY-MM-DD format
}

/**
 * Check if a date is today in IST
 * @param {string|Date} dateString - Date to check
 * @returns {boolean} True if the date is today in IST
 */
export function isTodayIST(dateString) {
  if (!dateString) return false;
  const today = getTodayIST();
  const dateIST = new Date(dateString).toLocaleDateString('en-CA', { timeZone: IST_TIMEZONE });
  return today === dateIST;
}

/**
 * Get relative time from now in IST (e.g., "2 hours ago")
 * @param {string|Date} dateString - Date to compare
 * @returns {string} Relative time string
 */
export function getRelativeTimeIST(dateString) {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) {
    return 'Just now';
  } else if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else {
    return formatDateIST(dateString);
  }
}

/**
 * Format order time for display (compact format)
 * @param {string|Date} dateString - Date to format
 * @returns {string} Formatted order time
 */
export function formatOrderTimeIST(dateString) {
  if (!dateString) return '';

  return new Date(dateString).toLocaleString('en-IN', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    day: 'numeric',
    month: 'short'
  });
}

export default {
  formatDateIST,
  formatTimeIST,
  formatDateTimeIST,
  getTodayIST,
  isTodayIST,
  getRelativeTimeIST,
  formatOrderTimeIST,
  IST_TIMEZONE
};
